import {customElement} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import type {Nullable} from "../../../bootstrap/common/types";
import {relativize} from "../../../common/utils/url";
import {trackingTextlinkInteract} from "../../../tracking/trackingAttributes";
import {generateUuid} from "../../../common/crypto";
import {RenderedDialogContent, RenderedDialogs} from "../renderedDialogs";

export class LightboxContent extends RenderedDialogContent {

    public constructor(
        dialogId: string,
        public readonly headline: string | undefined,
        public readonly subheadline: string | undefined,
        public readonly url: string,
        public readonly lightboxImageUrl: string,
        public readonly mediaType: string,
        public readonly imgAltText: string,
        public readonly buttonLabel: string
    ) {
        super(dialogId);
    }
}

@customElement("eop-news-search-lightbox-dialogs")
export class EopNewsSearchLightboxDialogs extends RenderedDialogs<LightboxContent> {

    protected readonly dialogClass = "lightbox-dialog";

    public constructor() {
        super();
    }

    protected renderDialogContent(lightbox: LightboxContent): TemplateResult {
        return html`
            <div class="layer-section layer-section-background-white">
                ${this.renderHeadline(lightbox.headline)}
                ${this.renderImageElement(lightbox.mediaType, lightbox.lightboxImageUrl, lightbox.imgAltText)}
                <div class="caption">
                    ${this.renderSubheadline(lightbox.subheadline)}
                    ${this.renderDownloadLink(lightbox.url, lightbox.buttonLabel)}
                </div>
            </div>
        `;
    }

    private renderHeadline(headline?: string): Nullable<TemplateResult> {
        if (!headline) {
            return null;
        }

        return html`
            <h3 class="h">${headline}</h3>
        `;
    }

    private renderImageElement(mediaType: string, lightboxImageUrl: string, imgAltText: string): TemplateResult {
        if (mediaType === "responsiveImage") {
            return html`
                <eop-responsive-image
                        .imageSrc=${lightboxImageUrl}
                        .imageAlt=${imgAltText}
                        intrinsic-aspect-ratio
                ></eop-responsive-image>
            `;
        }
        return html`
            <eop-plain-image
                    src=${lightboxImageUrl}
                    alt=${imgAltText}
                    class="eop-image"
                    intrinsic-aspect-ratio
            ></eop-plain-image>
        `;
    }

    private renderSubheadline(subheadline?: string): Nullable<TemplateResult> {
        if (!subheadline) {
            return null;
        }

        return html`
            <span class="caption-text">${subheadline}</span>
        `;
    }

    private renderDownloadLink(url: string, buttonLabel: string): TemplateResult {
        const relativeUrl = relativize(url);
        return html`
            <span class="image-download-link">
                <a href=${relativeUrl} download class="link media-link" ${trackingTextlinkInteract(buttonLabel, relativeUrl)}>${buttonLabel}</a>
            </span>
        `;
    }

    public create(
        headline: string | undefined,
        subheadline: string | undefined,
        url: string, lightboxImageUrl: string,
        mediaType: string,
        imgAltText: string,
        buttonLabel: string): string {
        const dialogId = "lightbox-dialog-" + generateUuid();
        const lightbox = new LightboxContent(
            dialogId,
            headline,
            subheadline,
            url,
            lightboxImageUrl,
            mediaType,
            imgAltText,
            buttonLabel
        );
        this.dialogContents = [...this.dialogContents, lightbox];
        return dialogId;
    }
}